import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import interactionResponse from 'await-interaction-response';
import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import {
    Button, Col,
    Text,
} from 'reaxl';
import {
    sendClick,
    sendImpressions,
} from 'reaxl-analytics';

import { getFilterAtIndex, getFilterSet } from '@/utilities/filterSuggestionHierarchy';

import { inlineFilterPromoClick, inlineFilterPromoImpression } from '@/analytics/srp/srpSearchEvents';

import { getFeatureDescription } from '@/config/vehicleFeatureDescriptions';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import TruckCabSizeFilter from '@/components/filters/TruckCabSizeFilter';
import PromoCard from '@/components/PromoCard/PromoCard';

const Carousel = dynamic(() => import(
    /* webpackChunkName: "IrisCarousel" */
    '@iris/react-components'
).then((mod) => mod.Carousel));

/**
 * Convert a filter set item to card configuration
 */
const convertFilterToCardConfig = (filter) => {
    if (!filter) return null;

    switch (filter.type) {
        case 'strongAttribute':
            return {
                type: 'strongAttribute',
                promptText: `${filter.data?.name}?`,
                filterValue: filter.data?.codes,
                showPrefixText: true,
            };

        case 'truck':
            return {
                type: 'truck',
                promptText: 'How many passenger seats do you need in your truck?',
                filterValue: {},
                showPrefixText: false,
            };

        case 'suv':
            return {
                type: 'suv',
                promptText: 'Do you need 3rd row seating?',
                filterValue: { featureCode: ['1306'] },
                showPrefixText: false,
            };

        case 'vehicleFeature':
            return {
                type: 'vehicleFeature',
                promptText: `${filter.data?.label}?`,
                filterValue: { featureCode: [filter.data?.value] },
                showPrefixText: true,
            };

        case 'suggested':
            return {
                type: 'suggested',
                promptText: `${filter.data?.name}?`,
                filterValue: filter.data?.codes,
                showPrefixText: true,
            };

        default:
            return null;
    }
};

/**
 * Generate data-cmp attribute based on filter type
 */
const getDataCmp = (filterType) => {
    const cmpMap = {
        strongAttribute: 'filter-inline-strong',
        truck: 'filter-inline-truck',
        suv: 'filter-inline-suv',
        vehicleFeature: 'filter-inline-feature',
        suggested: 'filter-inline-suggested',
    };
    return cmpMap[filterType] || 'filter-inline-suggested';
};

/**
 * Generate card configuration based on hierarchy data
 */

const getFilterDataCmp = ({
    firstStrongAttribute,
    isTruckCard,
    isSUVCard,
    isVehicleFeatureCard,
} = {}) => {
    if (firstStrongAttribute) return 'filter-inline-strong';
    if (isTruckCard) return 'filter-inline-truck';
    if (isSUVCard) return 'filter-inline-suv';
    if (isVehicleFeatureCard) return 'filter-inline-feature';
    return 'filter-inline-suggested';
};

/**
 * Renders Inline Filter Promo Card in carousel mode showing up to 10 filters.
 * Filters are displayed in priority order:
 * 1) Strong Attribute filter
 * 2) Truck/SUV Bodystyle substyle (Truck cab size filter, SUV 3rd row filter)
 * 3) Vehicle Features
 * 4) Suggested filter
 */
function InlineFilterPromoCard({ hierarchyData, filterSetParams }) {
    const router = useRouter();
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const navigateToSrp = useSrpNavigation();

    // State to track the current active filter set (base 10 filters)
    const [activeFilterSet, setActiveFilterSet] = useState([]);
    // State to track the next batch index for adding new filters
    const [nextBatchIndex, setNextBatchIndex] = useState(0);

    // Get filter set for carousel
    const filterSet = useMemo(() => {
        if (!hierarchyData || !filterSetParams) return [];

        // Use the active filter set if it exists, otherwise get initial 10 filters
        if (activeFilterSet.length > 0) {
            return activeFilterSet;
        }
        // Get initial 10 filters - don't set state in useMemo to avoid infinite loop
        return getFilterSet(filterSetParams);

    }, [hierarchyData, filterSetParams, activeFilterSet]);

    // Initialize active filter set when component mounts or params change
    useEffect(() => {
        if (hierarchyData && filterSetParams && activeFilterSet.length === 0) {
            const initialFilters = getFilterSet(filterSetParams);
            setActiveFilterSet(initialFilters);
        }
    }, [hierarchyData, filterSetParams, activeFilterSet.length]);

    const cardConfigs = useMemo(() => filterSet.map(convertFilterToCardConfig).filter(Boolean), [filterSet]);
    const filterDataCmp = useMemo(() => getFilterDataCmp(hierarchyData || {}), [hierarchyData]);
    const shouldRenderCard = cardConfigs.length > 0;

    const prevUrlRef = useRef(null);

    const impressionData = useMemo(() => ({
        name: inlineFilterPromoImpression,
        data: {
            pixallData: {
                cmp: filterDataCmp,
                featureName: cardConfigs?.[0]?.promptText || '',
            },
        },
    }), [cardConfigs, filterDataCmp]);

    // Reset state when filterSetParams change (new search)
    useEffect(() => {
        setActiveFilterSet([]);
        setNextBatchIndex(0);
    }, [filterSetParams]);

    // Helper function to handle filter replacement logic
    const replaceFilterAtIndex = useCallback((cardIndex) => {
        setActiveFilterSet((currentSet) => {
            // If it's the last card, reset to initial filter set (loop back to beginning)
            if (currentSet.length <= 1) {
                const initialFilters = getFilterSet(filterSetParams);
                setNextBatchIndex(0);
                return initialFilters;
            }

            const updatedSet = [...currentSet];
            updatedSet.splice(cardIndex, 1);

            // Try to add a new card from the next batch
            const usedFilters = new Set(updatedSet.map((f) => `${f.type}-${JSON.stringify(f.data)}`));
            const nextFilter = getFilterAtIndex(10 + nextBatchIndex, filterSetParams, usedFilters);

            if (nextFilter) {
                updatedSet.push(nextFilter);
            }

            return updatedSet;
        });

        // Increment the next batch index for future replacements (unless we're resetting)
        if (activeFilterSet.length > 1) {
            setNextBatchIndex((prev) => prev + 1);
        }
    }, [filterSetParams, nextBatchIndex, activeFilterSet.length]);

    // Mark filter as seen and fire impression only once per session per filter
    useEffect(() => {
        const currentUrl = router.asPath;
        // To make sure the impression only fires 1 time after card re-renders based on URL changes
        if (prevUrlRef.current !== currentUrl && shouldRenderCard) {
            sendImpressions(impressionData);
            prevUrlRef.current = currentUrl;
        }

    }, [router.asPath, impressionData, shouldRenderCard]);

    const handleButtonClick = useCallback(async (event, filterValue, customCardConfig = null) => {
        const { value } = event.target;
        await interactionResponse();
        event.target.value = value;

        const activeCardConfig = customCardConfig;
        const activeFilterDataCmp = customCardConfig
            ? getFilterDataCmp({ [`is${customCardConfig.type.charAt(0).toUpperCase() + customCardConfig.type.slice(1)}Card`]: true })
            : filterDataCmp;

        sendClick(inlineFilterPromoClick, event, {
            pixallData: {
                cmp: activeFilterDataCmp,
                featureName: activeCardConfig?.promptText || '',
            },
        });

        await navigateToSrp({
            resetPagination: true,
            scroll: true,
            isNewSearch: true,
            filtersValues: filterValue,
        });
    }, [filterDataCmp, navigateToSrp]);

    const handleNoPreferenceButtonClick = useCallback(async (event, customCardConfig = null, cardIndex = null) => {
        const { value } = event.target;
        await interactionResponse();
        event.target.value = value;

        const activeCardConfig = customCardConfig;
        const activeFilterDataCmp = customCardConfig
            ? getFilterDataCmp({ [`is${customCardConfig.type.charAt(0).toUpperCase() + customCardConfig.type.slice(1)}Card`]: true })
            : filterDataCmp;

        sendClick(inlineFilterPromoClick, event, {
            pixallData: {
                cmp: activeFilterDataCmp,
                featureName: activeCardConfig?.promptText || '',
                isNoPreference: true,
            },
        });

        // Replace the dismissed card with a new one
        replaceFilterAtIndex(cardIndex);
    }, [filterDataCmp, replaceFilterAtIndex]);

    const callToAction = useCallback((filterValue, cardType, isTruck = false, customCardConfig = null, cardIndex = null) => {
        if (isTruck) {
            return (
                <TruckCabSizeFilter
                    enableDropdown
                    onOptionChange={(event) => handleButtonClick(event, { bodyStyleSubtypeCode: event?.target?.value }, customCardConfig)}
                />
            );
        }
        return (
            <div className={clsx({
                'display-flex margin-top-4': isXs,
                'display-flex flex-column': !isXs,
            })}
            >
                <Button
                    bsSize="small"
                    bsStyle="primary"
                    onClick={(event) => handleButtonClick(event, filterValue, customCardConfig)}
                    className={clsx({
                        'margin-right-4 flex-1': isXs,
                        'margin-bottom-4 margin-top-4 width-100': !isXs,
                    })}
                >
                    Yes, Update Filter
                </Button>
                <Button
                    bsSize="small"
                    bsStyle="default"
                    onClick={(event) => handleNoPreferenceButtonClick(event, customCardConfig, cardIndex)}
                    className={clsx({
                        'flex-1': isXs,
                        'width-100': !isXs,
                    })}
                >
                    No Preference
                </Button>
            </div>
        );
    }, [handleButtonClick, handleNoPreferenceButtonClick, isXs]);

    // Early return if no hierarchyData is provided
    if (!hierarchyData) {
        return null;
    }

    const renderCardBody = (promptText, filterValue, showPrefixText = false, cardType = null, isTruck = false, customCardConfig = null, cardIndex = null) => {
        if (cardType === 'vehicleFeature') {
            // Extract feature name from prompt text (remove "Do you want " and "?")
            const featureName = promptText.replace('Do you want ', '').replace('?', '');
            const featureDescription = getFeatureDescription(featureName);

            return (
                <>
                    <Text
                        color="primary"
                        componentClass="div"
                        size={200}
                    >
                        Do you want
                    </Text>
                    <Text
                        className="margin-top-1"
                        color="primary"
                        componentClass="div"
                        size={400}
                        weight="bold"
                    >
                        {featureName}
                        ?
                    </Text>
                    {featureDescription && (
                        <Text
                            className="margin-top-2"
                            color="neutral-secondary"
                            componentClass="div"
                            size={200}
                        >
                            {featureDescription}
                        </Text>
                    )}
                    {callToAction(filterValue, cardType, isTruck, customCardConfig, cardIndex)}
                </>
            );
        }

        return (
            <>
                {showPrefixText && (
                    <Text
                        className="margin-top-3"
                        color="primary"
                        componentClass="div"
                        size={200}
                    >
                        {cardType === 'vehicleFeature' ? 'Do you want' : 'Only interested in vehicles that are'}
                    </Text>
                )}
                <Text
                    className={clsx({ 'margin-top-3': !showPrefixText })}
                    color="primary"
                    componentClass="div"
                    size={400}
                    weight="bold"
                >
                    {promptText}
                </Text>

                {callToAction(filterValue, cardType, isTruck, customCardConfig, cardIndex)}
            </>
        );
    };

    // Early return if card shouldn't render
    if (!shouldRenderCard) {
        return null;
    }

    // Render filter cards in a carousel
    return (
        <Col
            xs={12}
            sm={6}
            md={4}
            className="display-flex flex-column margin-bottom-4"
            data-cmp="filter-inline-carousel"
        >
            <Carousel
                loop={false}
                itemsInView={1}
                dragFree={false}
                animation="scroll"
                autoAdvance={false}
                showFadeEdge={false}
                deviceType={isXs ? 'mobile' : 'desktop'}
                showItemPreview={isXs}
                hideCarouselButtonOnDisable={false}
                carouselButtonVariant="floating"
                className="flex-1 display-flex flex-column"
            >
                {cardConfigs.map((config, index) => {
                    const filterType = config.type;
                    const isTruck = filterType === 'truck';
                    const dataCmp = getDataCmp(filterType);

                    // Generate unique key based on filter content instead of index
                    const uniqueKey = config.type === 'vehicleFeature'
                        ? `filter-${config.type}-${config.filterValue?.featureCode?.[0] || index}`
                        : `filter-${config.type}-${JSON.stringify(config.filterValue).substring(0, 20)}-${index}`;

                    return (
                        <div
                            key={uniqueKey}
                            className="height-100"
                            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                        >
                            <PromoCard
                                className="flex-1"
                                data-cmp={dataCmp}
                                panelClassName="bg-brand-secondary height-100 display-flex flex-column"
                            >
                                {renderCardBody(
                                    config.promptText,
                                    config.filterValue,
                                    config.showPrefixText,
                                    config.type,
                                    isTruck,
                                    config,
                                    index
                                )}
                            </PromoCard>
                        </div>
                    );
                })}
            </Carousel>
        </Col>
    );
}

InlineFilterPromoCard.propTypes = {
    hierarchyData: PropTypes.object.isRequired,
    filterSetParams: PropTypes.object.isRequired,
};

export default memo(InlineFilterPromoCard);
